/**
 * AdmissionsSection (program-details adapter) — renders /executive's admissions
 * band under this page's own section heading.
 *
 * The comparison cards, email form and click tracking have to match production,
 * so the band itself stays the shared section. Only the heading differs: this
 * page replaces the band's centered "Apply for Free" headline with the
 * divider-and-left-aligned title every other section uses, hence
 * `showHeadline={false}` plus a matching blue block above so the two read as one
 * band. Passing the `performance` variant would add an extra headline pair,
 * which /executive does not show.
 */
import { Container } from '@educator/components/ProgramSections/Container'
import { PROGRAM_SECTIONS_I18N_NAMESPACE } from '@educator/components/ProgramSections/i18n'
import { AdmissionsSection as ExecutiveAdmissionsSection } from '@educator/features/EnrollmentPage/sections/AdmissionsSection'
import { useEnrollmentCtaConfig } from '@educator/hooks/useEnrollmentCtaConfig'
import { useTranslation } from '@mc/i18n'

export const AdmissionsSection = () => {
  const { t } = useTranslation(PROGRAM_SECTIONS_I18N_NAMESPACE)
  const { primaryCta, isPrimaryLogIn } = useEnrollmentCtaConfig()

  return (
    /* The blue fills the 1380 block rather than the viewport, and the side inset
       moves inside it, so the heading lands on the same 100px gutter as every
       other section's content. `bleed` because the inset belongs to the inner
       wrapper here, not to the Container. */
    <Container bleed className='bg-ja-blue text-edu-fg-primary'>
      {/* One inset for the whole panel, so the comparison card's edge lines up
          with the heading above it. `withMobileInset={false}` drops the band's
          own mobile padding, which would otherwise push the card 18.5px further
          in than the heading.

          `section`, not `div`: a `header` whose nearest sectioning ancestor is
          the body maps to the `banner` landmark, and the global nav already
          owns that role — two banners on one page is a WCAG 1.3.1 failure
          (STA-27). Every other redesigned section scopes its own `header` the
          same way; this one reached for `Container` alone and missed it. The
          element stays unnamed, so it is a scoping boundary rather than an
          extra `region` landmark, and `section` is display:block like the
          `div` it replaces — no layout change. */}
      <section className='px-6 md:px-25'>
        {/* The rule is white/30 (edu-fg-dimmed) rather than the neutral stroke
            every other section uses, which would disappear against the blue. */}
        <header className='flex flex-col gap-3 py-6 md:py-12'>
          <div className='border-t border-edu-fg-dimmed' />
          <div className='flex items-center justify-between md:min-h-12'>
            <h2 className='m-0 text-xl leading-tight font-semibold md:text-title-3 md:leading-title-3'>
              {t('admissions.headline')}
            </h2>
          </div>
        </header>

        <ExecutiveAdmissionsSection
          primaryCtaLabel={primaryCta.label}
          primaryCtaHref={primaryCta.href}
          isPrimaryLogIn={isPrimaryLogIn}
          showHeadline={false}
          withMobileInset={false}
        />
      </section>
    </Container>
  )
}
